import { format } from 'date-fns';
import { Calendar, Check } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Calendar as CalendarPicker } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export type DatePreset = 'today' | 'this_week' | 'this_month' | 'last_month' | 'all' | 'custom';

export interface DateFilterValue {
  preset: DatePreset;
  customFrom?: Date;
  customTo?: Date;
}

interface DateFilterProps {
  value: DateFilterValue;
  onChange: (value: DateFilterValue) => void;
}

const presetLabels: Record<Exclude<DatePreset, 'custom'>, string> = {
  today: 'Today',
  this_week: 'This Week',
  this_month: 'This Month',
  last_month: 'Last Month',
  all: 'All Time',
};

function getDisplayLabel(value: DateFilterValue): string {
  if (value.preset === 'custom') {
    if (value.customFrom && value.customTo) {
      return `${format(value.customFrom, 'dd/MM/yy')} - ${format(value.customTo, 'dd/MM/yy')}`;
    }
    if (value.customFrom) {
      return `From ${format(value.customFrom, 'dd/MM/yy')}`;
    }
    if (value.customTo) {
      return `Until ${format(value.customTo, 'dd/MM/yy')}`;
    }
    return 'Custom Range';
  }
  return presetLabels[value.preset];
}

export function DateFilter({ value, onChange }: DateFilterProps) {
  const [open, setOpen] = useState(false);
  const [showCustomPicker, setShowCustomPicker] = useState(value.preset === 'custom');
  const [selectingField, setSelectingField] = useState<'from' | 'to'>('from');

  const handlePresetSelect = (preset: Exclude<DatePreset, 'custom'>) => {
    onChange({ preset, customFrom: undefined, customTo: undefined });
    setShowCustomPicker(false);
    setOpen(false);
  };

  const handleCustomSelect = () => {
    setShowCustomPicker(true);
    onChange({ preset: 'custom', customFrom: value.customFrom, customTo: value.customTo });
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (!date) return;

    // Set to noon UTC to avoid timezone issues
    const noonUtc = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0),
    );

    if (selectingField === 'from') {
      // If selecting from date and it's after the to date, clear the to date
      const newTo = value.customTo && noonUtc > value.customTo ? undefined : value.customTo;
      onChange({ preset: 'custom', customFrom: noonUtc, customTo: newTo });
      setSelectingField('to');
    } else {
      // If selecting to date and it's before the from date, swap them
      if (value.customFrom && noonUtc < value.customFrom) {
        onChange({ preset: 'custom', customFrom: noonUtc, customTo: value.customFrom });
      } else {
        onChange({ preset: 'custom', customFrom: value.customFrom, customTo: noonUtc });
      }
      setOpen(false);
      setSelectingField('from');
    }
  };

  const handleClearCustom = () => {
    onChange({ preset: 'custom', customFrom: undefined, customTo: undefined });
    setSelectingField('from');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-[180px] justify-between">
          <span className="truncate">{getDisplayLabel(value)}</span>
          <Calendar className="ml-2 h-4 w-4 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0">
        <div className="flex">
          {/* Preset options */}
          <div className="border-r p-2">
            <div className="space-y-1">
              {(Object.entries(presetLabels) as [Exclude<DatePreset, 'custom'>, string][]).map(
                ([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => handlePresetSelect(key)}
                    className={cn(
                      'flex w-full items-center justify-between rounded-md px-3 py-2 text-sm hover:bg-accent',
                      value.preset === key && !showCustomPicker && 'bg-accent',
                    )}
                  >
                    <span>{label}</span>
                    {value.preset === key && !showCustomPicker && (
                      <Check className="ml-2 h-4 w-4" />
                    )}
                  </button>
                ),
              )}
              <div className="my-2 border-t" />
              <button
                type="button"
                onClick={handleCustomSelect}
                className={cn(
                  'flex w-full items-center justify-between rounded-md px-3 py-2 text-sm hover:bg-accent',
                  showCustomPicker && 'bg-accent',
                )}
              >
                <span>Custom Range</span>
                {showCustomPicker && <Check className="ml-2 h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* Calendar picker (shown when custom is selected) */}
          {showCustomPicker && (
            <div className="p-3">
              <div className="mb-3 flex items-center gap-2 text-sm">
                <button
                  type="button"
                  onClick={() => setSelectingField('from')}
                  className={cn(
                    'rounded-md border px-3 py-1.5',
                    selectingField === 'from' ? 'border-primary bg-primary/10' : 'border-input',
                  )}
                >
                  {value.customFrom ? format(value.customFrom, 'dd/MM/yyyy') : 'Start date'}
                </button>
                <span className="text-muted-foreground">→</span>
                <button
                  type="button"
                  onClick={() => setSelectingField('to')}
                  className={cn(
                    'rounded-md border px-3 py-1.5',
                    selectingField === 'to' ? 'border-primary bg-primary/10' : 'border-input',
                  )}
                >
                  {value.customTo ? format(value.customTo, 'dd/MM/yyyy') : 'End date'}
                </button>
                {(value.customFrom || value.customTo) && (
                  <button
                    type="button"
                    onClick={handleClearCustom}
                    className="ml-auto text-xs text-muted-foreground hover:text-foreground"
                  >
                    Clear
                  </button>
                )}
              </div>
              <CalendarPicker
                mode="single"
                selected={selectingField === 'from' ? value.customFrom : value.customTo}
                onSelect={handleDateSelect}
                captionLayout="dropdown"
                startMonth={new Date(2020, 0)}
                endMonth={new Date()}
                disabled={(date) => date > new Date()}
                autoFocus
              />
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
